import { AppPageLayout } from "@/components/common/AppPageLayout";
import { patchPatientDiariesCache } from "@/features/diaries/api/diaryInvalidation";
import { diaryByIdQueryOptions } from "@/features/diaries/api/diaryQueryOptions";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import { DiaryLockCall } from "@/features/diaries/components/DiaryLockCall";
import { DiaryEditDisabledProvider } from "@/features/diaries/context/DiaryEditDisabledContext";
import { isPatientDiaryHomePath } from "@/features/diaries/utils/patientHomePortal";
import JourneyProgress from "@/features/onboarding/components/JourneyProgress";
import { Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import ErrorPage from "../../../components/pages/ErrorPage";

const DiaryId = () => {
    const { diaryId } = Route.useParams();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const editDisabled = Boolean(diary?.locked_at);
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    // Set-up steps render progress under their page title (PatientSetupPage). Consent inside set-up does not,
    // so it takes the layout card.
    const isPatientSetup = /\/diaries\/[^/]+\/setup(?:\/(?!consent\/)|$)/.test(pathname);
    // Home + setup steps render progress under their page title.
    const showLayoutJourneyProgress = !isPatientDiaryHomePath(pathname) && !isPatientSetup;

    const content = (
        <>
            {diary?.locked_at ? <DiaryLockCall diary={diary} className="mb-4" /> : null}
            {showLayoutJourneyProgress ? <JourneyProgress diary={diary} /> : null}
            <Outlet />
        </>
    );

    return (
        <DiaryEditDisabledProvider disabled={editDisabled}>
            <AppPageLayout as="div" className="flex flex-col flex-1 gap-y-6 md:py-8 min-h-full">
                {content}
            </AppPageLayout>
        </DiaryEditDisabledProvider>
    );
};
export const Route = createFileRoute("/_app/diaries/$diaryId")({
    beforeLoad: ({ context }) => {
        return {
            auth: context.auth,
        };
    },
    loader: async ({ context, params }) => {
        const diary = await context?.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        if (diary?.patient_id) {
            patchPatientDiariesCache(context.queryClient, diary, diary.patient_id);
        }
        return { diary };
    },
    component: DiaryId,
    errorComponent: ({ error, reset }) => <ErrorPage title="Diary Error" error={error} reset={reset} />,
});
